;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import * as Sentry from "@sentry/nextjs";
import type { NextRequest } from "next/server";
import { isExpectedClientFailure } from "@/lib/observability/is-expected-client-failure";
import {
	type AppLogLevel,
	emitLog,
	toScrubbedLogAttributes,
} from "@/lib/observability/log";
import {
	scrubSensitiveString,
	scrubSensitiveValue,
} from "@/lib/observability/scrub";

export enum LogLevel {
	DEBUG = "debug",
	INFO = "info",
	WARN = "warn",
	ERROR = "error",
}

export interface LogContext {
	requestId?: string;
	userId?: string;
	organizationId?: string;
	workspaceId?: string;
	feature?: string;
	operation?: string;
	/** Also raise a `warn` as an Issue (for warnings someone should be paged on). */
	reportToSentry?: boolean;
	/** Extra tags for this one Issue event. */
	tags?: Record<string, string>;
	fingerprint?: string[];
	[key: string]: unknown;
}

export interface SpanData {
	attributes?: Record<string, string | number | boolean | undefined>;
	name: string;
	op: string;
}

export interface RequestLogContext {
	organizationId?: string;
	userId?: string;
	workspaceId?: string;
}

const TENANT_ATTRIBUTE_KEYS = {
	organizationId: "tenant.organization_id",
	workspaceId: "tenant.workspace_id",
} as const;

const MAX_ERROR_MESSAGE_LENGTH = 512;

/** Telemetry must never break the code path that is logging. */
function safely(callback: () => void): void {
	try {
		callback();
	} catch {
		// Ignore: SDK missing, not initialised, or partly mocked in tests.
	}
}

/**
 * Callers pass `error as Error` from a `catch`, so the value can be anything.
 * Objects keep their message, or their status, so they don't all group as
 * "[object Object]".
 */
function toError(value: unknown): Error {
	if (value instanceof Error) {
		return value;
	}
	if (value && typeof value === "object") {
		if ("message" in value && typeof value.message === "string") {
			return new Error(value.message);
		}
		if ("status" in value && typeof value.status === "number") {
			return new Error(`Request failed with status ${value.status}`);
		}
		return new Error("Non-Error value thrown");
	}
	return new Error(String(value));
}

function buildIssueScope({
	context,
	message,
	scrubbedContext,
}: {
	context: LogContext | undefined;
	message: string;
	scrubbedContext: unknown;
}) {
	return {
		extra: { context: scrubbedContext, message },
		...(context?.fingerprint && { fingerprint: context.fingerprint }),
		tags: {
			feature: context?.feature,
			operation: context?.operation,
			"request.id": context?.requestId,
			"tenant.organization_id": context?.organizationId,
			"tenant.workspace_id": context?.workspaceId,
			...context?.tags,
		},
		...(context?.userId && { user: { id: context.userId } }),
	};
}

function toSeverity(level: AppLogLevel): Sentry.SeverityLevel {
	return level === "warn" ? "warning" : level;
}

class Logger {
	private log({
		context,
		error,
		level,
		message,
	}: {
		context?: LogContext;
		error?: Error;
		level: AppLogLevel;
		message: string;
	}) {
		const normalizedError = error == null ? undefined : toError(error);
		// Scrubbed once: the log, the breadcrumb and the Issue all reuse it.
		const scrubbedContext = scrubSensitiveValue(context) as
			| LogContext
			| undefined;
		const attributes = toScrubbedLogAttributes(scrubbedContext);
		if (normalizedError) {
			attributes["error.type"] = normalizedError.name;
			// The console copy is written before `beforeSendLog` scrubs.
			attributes["error.message"] = scrubSensitiveString(
				normalizedError.message,
			).slice(0, MAX_ERROR_MESSAGE_LENGTH);
		}
		emitLog({ attributes, level, message });
		safely(() =>
			this.report({
				context,
				error: normalizedError,
				level,
				message,
				scrubbedContext,
			}),
		);
	}

	private report({
		context,
		error,
		level,
		message,
		scrubbedContext,
	}: {
		context?: LogContext;
		error?: Error;
		level: AppLogLevel;
		message: string;
		scrubbedContext: LogContext | undefined;
	}) {
		if (level === "error") {
			const scope = {
				...buildIssueScope({ context, message, scrubbedContext }),
				level: "error" as const,
			};
			if (error) {
				Sentry.captureException(error, scope);
			} else {
				Sentry.captureMessage(message, scope);
			}
			return;
		}

		if (level === "warn" && context?.reportToSentry) {
			Sentry.captureMessage(message, {
				...buildIssueScope({ context, message, scrubbedContext }),
				level: "warning",
			});
			return;
		}

		// Breadcrumbs give the next Issue event its lead-up.
		Sentry.addBreadcrumb({
			category: "logger",
			data: scrubbedContext,
			level: toSeverity(level),
			message,
		});
	}

	public debug(message: string, context?: LogContext) {
		this.log({ context, level: "debug", message });
	}

	public info(message: string, context?: LogContext) {
		this.log({ context, level: "info", message });
	}

	public warn(message: string, context?: LogContext) {
		this.log({ context, level: "warn", message });
	}

	public error(message: string, error?: Error, context?: LogContext) {
		this.log({ context, error, level: "error", message });
	}

	public captureUnexpected(
		message: string,
		error: unknown,
		context?: LogContext,
	) {
		if (isExpectedClientFailure(error)) {
			this.addBreadcrumb(message, "expected-failure", context, "warning");
			return;
		}
		this.error(message, toError(error), context);
	}

	public startSpan<T>(
		spanData: SpanData,
		callback: (span: Sentry.Span) => T,
	): T {
		return Sentry.startSpan(spanData, callback);
	}

	public async startSpanAsync<T>(
		spanData: SpanData,
		callback: (span: Sentry.Span) => Promise<T>,
	): Promise<T> {
		return Sentry.startSpan(spanData, callback);
	}

	public addBreadcrumb(
		message: string,
		category: string,
		data?: Record<string, unknown>,
		level: Sentry.SeverityLevel = "info",
	) {
		safely(() =>
			Sentry.addBreadcrumb({
				category,
				data: scrubSensitiveValue(data) as Record<string, unknown> | undefined,
				level,
				message,
			}),
		);
	}

	public setUser(
		user: {
			id?: string;
			email?: string;
			username?: string;
			organizationId?: string;
			workspaceId?: string;
		} | null,
	) {
		safely(() => {
			if (user === null) {
				Sentry.setUser(null);
				return;
			}
			Sentry.setUser({
				email: user.email,
				id: user.id,
				username: user.username,
			});
		});
	}

	/** Pass `undefined` to clear a tag. */
	public setTag(key: string, value: string | undefined) {
		safely(() => Sentry.setTag(key, value));
	}

	/**
	 * Tenant ids must be searchable on Issues and on logs, spans and metrics.
	 * Streamed spans ignore scope tags, so mirror them as scope attributes.
	 */
	public setTenantTag(
		key: keyof typeof TENANT_ATTRIBUTE_KEYS,
		value: string | undefined,
	) {
		const attributeKey = TENANT_ATTRIBUTE_KEYS[key];
		safely(() => {
			Sentry.setTag(attributeKey, value);
			if (value === undefined) {
				Sentry.getIsolationScope?.().removeAttribute?.(attributeKey);
				return;
			}
			Sentry.setAttribute?.(attributeKey, value);
		});
	}

	public setContext(name: string, context: Record<string, unknown> | null) {
		safely(() => Sentry.setContext(name, context));
	}

	public createRequestLogger(req: NextRequest) {
		const requestId =
			req.headers.get("x-request-id") ||
			req.headers.get("x-correlation-id") ||
			crypto.randomUUID();
		const correlationId = req.headers.get("x-correlation-id") || undefined;
		const requestContext: RequestLogContext = {};

		safely(() => {
			Sentry.setTag("request.id", requestId);
			Sentry.setAttribute?.("request.id", requestId);
			if (correlationId) {
				Sentry.setTag("correlation.id", correlationId);
			}
		});

		const bindContext = (nextContext: RequestLogContext) => {
			if (nextContext.userId) {
				requestContext.userId = nextContext.userId;
				safely(() => Sentry.setUser({ id: nextContext.userId }));
			}
			if (nextContext.organizationId) {
				requestContext.organizationId = nextContext.organizationId;
				this.setTenantTag("organizationId", nextContext.organizationId);
			}
			if (nextContext.workspaceId) {
				requestContext.workspaceId = nextContext.workspaceId;
				this.setTenantTag("workspaceId", nextContext.workspaceId);
			}
		};

		bindContext({
			organizationId: req.headers.get("x-organization-id") || undefined,
			userId: req.headers.get("x-user-id") || undefined,
			workspaceId: req.headers.get("x-workspace-id") || undefined,
		});

		const withRequestContext = (
			additionalContext?: LogContext,
		): LogContext => ({
			...requestContext,
			requestId,
			...additionalContext,
		});

		return {
			addBreadcrumb: (
				message: string,
				category: string,
				data?: Record<string, unknown>,
				level?: Sentry.SeverityLevel,
			) => this.addBreadcrumb(message, category, data, level),
			bindContext,
			captureUnexpected: (
				message: string,
				error: unknown,
				additionalContext?: LogContext,
			) =>
				this.captureUnexpected(
					message,
					error,
					withRequestContext(additionalContext),
				),
			debug: (message: string, additionalContext?: LogContext) =>
				this.debug(message, withRequestContext(additionalContext)),
			error: (message: string, error?: Error, additionalContext?: LogContext) =>
				this.error(message, error, withRequestContext(additionalContext)),
			info: (message: string, additionalContext?: LogContext) =>
				this.info(message, withRequestContext(additionalContext)),
			setContext: (name: string, context: Record<string, unknown> | null) =>
				this.setContext(name, context),
			setTag: (key: string, value: string | undefined) =>
				this.setTag(key, value),
			startSpan: <T>(
				spanData: SpanData,
				callback: (span: Sentry.Span) => T,
			): T => this.startSpan(spanData, callback),
			startSpanAsync: <T>(
				spanData: SpanData,
				callback: (span: Sentry.Span) => Promise<T>,
			): Promise<T> => this.startSpanAsync(spanData, callback),
			warn: (message: string, additionalContext?: LogContext) =>
				this.warn(message, withRequestContext(additionalContext)),
		};
	}
}

export const logger = new Logger();
